/**
 * RemoveRulesConfirmDialog: the beat before taking SKUs off Always 0.
 *
 * Removing an Always 0 rule puts real stock back on a storefront at the next
 * push, which may be a SKU someone meant to stop selling. So Remove asks
 * first whenever the selection holds an Always 0 row. A selection of Skip
 * rows only goes straight through (Shopify would show PrintRun's number,
 * which it was already allowed to), and the toast still offers Undo.
 *
 * The confirm is orange outline (DESIGN.md 13.6): reversible, not red.
 */
import { Loader2, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils';
import { REMOVE_RULE_CLASS } from './push-rule-styles';

interface RemoveRulesConfirmDialogProps {
  open: boolean;
  /** SKUs in the selection. */
  count: number;
  /** Base garments among them that are Always 0 now. */
  baseGarmentCount: number;
  applying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function RemoveRulesConfirmDialog({
  open,
  count,
  baseGarmentCount,
  applying,
  onConfirm,
  onCancel,
  onCloseAutoFocus,
}: RemoveRulesConfirmDialogProps) {
  const { t } = useTranslation('inventory');
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !applying) onCancel();
      }}
    >
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('stockSync.pushRules.remove.title', { count })}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-foreground">
              <p>{t('stockSync.pushRules.remove.body')}</p>
              {baseGarmentCount > 0 && (
                <p>{t('stockSync.pushRules.remove.baseGarments', { count: baseGarmentCount })}</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={applying}>{t('stockSync.pushRules.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={applying}
            aria-busy={applying || undefined}
            className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5', REMOVE_RULE_CLASS)}
            onClick={(event) => {
              event.preventDefault();
              if (!applying) onConfirm();
            }}
          >
            {applying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t('stockSync.pushRules.remove.action')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
